/**
 * Language translations for Islamic Cure & Care 2026
 * Supported: English (en) and Arabic (ar)
 */

export const TRANSLATIONS = {
  en: {
    // Header
    home: "Home",
    ai_search: "AI Search",
    hijama_atlas: "Hijama Atlas",
    services: "Services",
    ruqyah: "Ruqyah Library",
    academy: "Qur'an & Arabic Academy",
    sign_in: "Sign in",
    sign_out: "Sign out",
    products: "Products",
    contact: "Contact",
    about: "About",
    tagline: "Tibb al-Nabawi · 2026",

    // Disclaimer
    disclaimer_label: "Medical & Legal Notice",
    disclaimer_title: "A trust placed in your hands",
    disclaimer_intro_1: "Islamic Cure & Care 2026",
    disclaimer_intro_2:
      "is a scholarly reference combining authentic Tibb al-Nabawi (Prophetic Medicine) with peer-reviewed modern research. It is provided",
    disclaimer_intro_3: "strictly for educational and informational purposes",
    disclaimer_point_1_a: "This is",
    disclaimer_point_1_b: "not a substitute",
    disclaimer_point_1_c: "for professional medical diagnosis or treatment.",
    disclaimer_point_2_a: "Always consult a",
    disclaimer_point_2_b: "qualified physician",
    disclaimer_point_2_c: "before applying any remedy, especially during pregnancy, chronic illness, or while taking medication.",
    disclaimer_point_3_a: "Hijama (cupping) must only be performed by a",
    disclaimer_point_3_b: "certified practitioner",
    disclaimer_point_3_c: ".",
    disclaimer_point_4: "Only authentic (Sahih) Hadiths and verified Quranic verses are cited.",
    disclaimer_accept: "I understand and accept — Enter the Library",
    disclaimer_footnote: "By continuing, you acknowledge you have read and accept this notice.",

    // Home
    hero_title_1: "The ancient healing of the Prophet ﷺ,",
    hero_title_2: "verified by modern science.",
    hero_subtitle:
      "A scholarly archive of authentic Tibb al-Nabawi remedies — every entry rooted in Sahih Hadith and cross-referenced with peer-reviewed clinical research from 2017–2026.",
    cta_ai: "Ask the AI Researcher",
    cta_browse: "Browse the Library",
    stat_illnesses: "Illnesses",
    stat_hadiths: "Sahih Hadiths",
    stat_points: "Hijama Points",
    stat_studies: "Modern Studies",

    library_label: "The Library",
    library_title: "Explore by category — from the chronic to the spiritual.",

    pharmacy_label: "Prophetic Pharmacy",
    pharmacy_title: "Three remedies. Fourteen centuries of tradition.",
    pharmacy_subtitle:
      "From the Quran and Sunnah — preparations recorded by Ibn al-Qayyim and Ibn Sina.",

    ai_cta_pill: "AI-Powered Symptom Search",
    ai_cta_title:
      "Describe what you feel — receive guidance rooted in Quran, Sunnah, and modern medicine.",
    ai_cta_subtitle:
      "Our research assistant cross-references your symptoms across the entire database to return Quranic verses, authentic Hadiths, Hijama mappings, Prophetic remedies, and modern clinical studies.",
    ai_cta_btn: "Start a search",

    // Category page
    all_categories: "All categories",
    read_full: "Read full entry",
    no_illness_in_category: "No illnesses in this category yet.",

    // Illness detail
    back_to_category: "Back to category",
    page_disclaimer:
      "Medical Disclaimer: This entry is for educational purposes only. Always consult a qualified physician before applying any remedy or undergoing Hijama therapy. Pregnant women, children, and patients with chronic conditions require specialist guidance.",
    section_revelation: "Exhaustive Revelation",
    section_hijama_map: "Hijama Map",
    section_pharmacy: "Prophetic Pharmacy",
    section_lifestyle: "Sunnah Lifestyle",
    section_studies: "2026 Clinical Proof",
    studies_subtitle: "· Peer-reviewed modern research",
    label_quran: "Quran",
    label_sahih_hadith: "Sahih Hadith",
    label_preparation: "Preparation:",
    label_duration: "Duration:",
    label_source: "Source:",
    label_narrator: "Narrator:",
    sunnah_note: "indicates Sunnah-documented point",

    // Search page
    search_pill: "AI Research Assistant · Claude Sonnet 4.5",
    search_title: "Describe your symptoms.",
    search_subtitle:
      "The assistant searches authentic Hadith, Quran, Hijama mappings, Prophetic remedies, and modern peer-reviewed studies in our database — and returns relevant entries.",
    search_placeholder: "e.g., I have stomach pain and bloating after meals…",
    search_btn: "Search",
    searching: "Searching",
    try_these: "Try one of these",
    researching: "Researching the database…",
    researcher_note: "Researcher's note",
    suggested_entries: "Suggested entries from the library",
    no_results: "No direct matches found. Try rephrasing or browse categories from the home page.",
    search_failed: "Search failed. Please try again.",

    // Hijama Atlas
    atlas_pill: "200 Documented Points",
    atlas_title: "The Hijama Atlas",
    atlas_subtitle_1: "A classical mapping of cupping points compiled from Ibn al-Qayyim's",
    atlas_subtitle_2: "At-Tibb An-Nabawi",
    atlas_subtitle_3: ", Ibn Sina's",
    atlas_subtitle_4: "Al-Qanun fi al-Tibb",
    atlas_subtitle_5: ", and modern Hijama practice atlases. Sunnah points are marked with a gold star.",
    sunnah_only: "Sunnah points only",
    atlas_search_placeholder: "Search by name, region, or indication…",
    points_of: "of",
    points: "points",
    no_points_match: "No points match your filters.",
    region: "Region:",
    location: "Location:",

    // About
    method_label: "Our Method",
    about_title: "A library held to the highest scholarly standards.",
    about_intro:
      "Islamic Cure & Care 2026 is a living archive uniting authentic Prophetic Medicine (Tibb al-Nabawi) with modern peer-reviewed evidence. Below is the methodology behind every entry.",
    about_s1_t: "Authentic Sources Only",
    about_s1_b:
      "Every Hadith is verified as Sahih (authentic) through Sahih al-Bukhari, Sahih Muslim, Sunan Ibn Majah, Sunan Abi Dawud, and Jami at-Tirmidhi. We do not include weak (Da'if) or fabricated (Mawdu') reports. Quranic verses are quoted with full Surah and Ayah reference.",
    about_s2_t: "Classical Pharmacy",
    about_s2_b:
      "Preparation methods are drawn from Ibn al-Qayyim's At-Tibb An-Nabawi and Ibn Sina's Al-Qanun fi al-Tibb (The Canon of Medicine) — the foundational texts of Islamic medicine for over 1,000 years.",
    about_s3_t: "Modern Clinical Cross-Reference",
    about_s3_b:
      "Where applicable, every remedy is supported by a contemporary peer-reviewed study — citing journal, authors, year, and DOI — sourced from PubMed, NCBI, and university research repositories.",
    about_s4_t: "Strict Legal & Medical Compliance",
    about_s4_b:
      "This is an educational reference, not a medical practice. The disclaimer modal that gates this site is intentional. Hijama (cupping) must only be performed by a certified practitioner. Always consult a qualified physician.",
    about_verse_en: '"And when I am ill, it is He who cures me." — Quran 26:80',

    // Footer
    footer_intro:
      "A scholarly union of Tibb al-Nabawi (Prophetic Medicine) and modern evidence-based research. Every remedy is rooted in authentic Sahih Hadith and verified by peer-reviewed studies.",
    footer_explore: "Explore",
    footer_disclaimer_title: "Medical Disclaimer",
    footer_disclaimer_body_1: "Content provided for",
    footer_disclaimer_body_2: "educational purposes only",
    footer_disclaimer_body_3:
      ". Always consult a qualified healthcare professional before applying any remedy. Hijama (cupping) must be performed by a certified practitioner.",
    footer_copyright: "© 2026 Islamic Cure & Care · Built with reverence",
    footer_made_with: "Crafted with",
    footer_for_ummah: "for the Ummah",

    // Body Diagram
    diagram_label: "Visual Reference",
    diagram_title: "Hijama Point Diagram",
    print_protocol: "Print Protocol",
    front_view: "Front View",
    back_view: "Back View",
    hover_hint: "Hover over a point to see its name.",
    legend_treatment: "Treatment point",
    legend_sunnah: "Sunnah point",
    recommended_points: "Recommended points for this illness",
    diagram_disclaimer:
      "★ Sunnah-documented in authentic Hadith. Anatomical positions are approximate — always seek a certified Hijama practitioner.",

    // Ruqyah recommendation
    ruqyah_rec_title: "Recommended Ruqyah for your healing",
    ruqyah_rec_subtitle:
      "Authentic Quranic Ruqyah recordings from our clinic — listen with sincere du'a alongside the Prophetic remedies below.",

    // Today calendar
    today_label: "Today",

    // Install App prompt
    install_app_pill: "Save time",
    install_app_title: "Add Islamic Cure & Care to your Home Screen",
    install_app_cta: "Install",
    install_ios_title: "Install on your iPhone",
    install_ios_step1_a: "Tap the",
    install_ios_step1_b: "Share button at the bottom of Safari",
    install_ios_step2: "Scroll down and tap \"Add to Home Screen\"",
    install_ios_step3: "Tap \"Add\" — the Al-Aqsa icon will appear on your home screen",
    install_ios_close: "Got it, JazākAllāh",

    // Illness page extras
    illness_not_found: "Illness not found.",
    label_surah: "Surah",
    classical_sources_label: "Classical Medical Sources",
    source_label: "Source",
    atlas_page_label: "Atlas page",
    share_on_whatsapp: "Share on WhatsApp",
    share_label: "Share",
    share_copy_link: "Copy link",
    share_copied: "Link copied!",
    share_ig_copied: "Copied — paste in Instagram",
    add_to_calendar: "Add to my calendar",
    add_to_calendar_note: "Optional — get a reminder on your phone before each session.",

    // Service condition bullets (kept short & scannable)
    counselling_includes_1: "45–60 minute private session",
    counselling_includes_2: "Video (Zoom / WhatsApp) or in-person",
    counselling_includes_3: "Strict confidentiality — nothing shared",
    counselling_includes_4: "Guidance rooted in Qur'ān & Sunnah",
    nikah_includes_1: "Qualified ma'dhūn officiates the ceremony",
    nikah_includes_2: "Signed & stamped certificate provided",
    nikah_includes_3: "Khuṭbah + 2 witnesses arranged",
    nikah_includes_4: "In-clinic, at your venue, or hybrid",
    whats_included: "What's included",

    // Online Ruqyah Group (every Saturday)
    orgroup_pill: "New · Every Saturday",
    orgroup_title: "Online Ruqyah Group — Every Saturday",
    orgroup_subtitle:
      "Two weekly Sharī'ah-compliant group Ruqyah sessions delivered by our clinic — join from anywhere in the world.",
    orgroup_women_pill: "Session 1",
    orgroup_women_title: "Women-only session",
    orgroup_women_time: "Every Saturday · 4:00 PM UK time",
    orgroup_women_wa:
      "Assalāmu ʿalaykum, I would like to register for the Women-only Online Ruqyah Group (Saturdays 4 PM UK).",
    orgroup_men_pill: "Session 2",
    orgroup_men_title: "Men-only session",
    orgroup_men_time: "Every Saturday · 6:00 PM UK time",
    orgroup_men_wa:
      "Assalāmu ʿalaykum, I would like to register for the Men-only Online Ruqyah Group (Saturdays 6 PM UK).",
    orgroup_platform: "Via WhatsApp Video Call · Zoom · or Teams",
    orgroup_per_person: "per person, per session",
    orgroup_book_cta: "Register on WhatsApp",
    orgroup_register_notice:
      "Places are limited — please register 2–3 days in advance via WhatsApp so we can confirm your spot and send the meeting link. Registration is by WhatsApp only (call or text).",

    // Hijama Sunnah Days card
    sunnah_days_pill: "Upcoming Hijama Sunnah Days",
    sunnah_days_hadith:
      "\"Whoever wants to perform Hijama then let him search for the 17th, 19th and 21st days of the Islamic lunar month…\"",
    sunnah_days_hadith_ref: "— Saḥīḥ Sunan Ibn Mājah 3486",
    book_on_whatsapp: "Book on WhatsApp",
    copy_reminder: "Copy reminder",
    copied: "✓ Copied!",
    male_female_therapists: "male & female therapists",

    // Ruqyah programmes
    ruqyah_programmes_pill: "Self-Treatment Programmes",
    ruqyah_programmes_title: "Additional Ruqyah Programmes",
    ruqyah_programmes_subtitle:
      "Three traditional clinic-provided protocols for at-home self-treatment. Always combine with sincere du'ā and trust in Allāh as the only true Healer.",
    section_ingredients: "Ingredients",
    section_recite_each: "Recite each — 7 times — over the mixture",
    section_method: "Method",
    section_additional_step: "Additional Step",
    section_caution: "⚠️ Caution",
    ask_on_whatsapp: "Ask about this on WhatsApp",

    // Diagnostic CTA
    diagnostic_cta_pill: "Free spiritual diagnosis",
    diagnostic_cta_title: "Take the Ruqyah Diagnostic Questionnaire",
    diagnostic_cta_subtitle:
      "Answer 20 short questions about your symptoms — we'll send the summary straight to our WhatsApp for personalised guidance.",
    diagnostic_cta_btn: "Start the questionnaire",

    // Diagnostic page
    diag_back: "Back to Ruqyah Library",
    diag_title: "Ruqyah Diagnostic Questionnaire",
    diag_gender_label: "Gender *",
    diag_gender_male: "Male",
    diag_gender_female: "Female",

    // Islamic Marriage services (Nikah + Counselling)
    marriage_services_pill: "Islamic marriage services",
    marriage_services_title: "Islamic Marriage — Counselling & Nikah",
    marriage_services_subtitle:
      "Two Sharī'ah-compliant services delivered by our clinic — book instantly on WhatsApp.",
    counselling_title: "Islamic Marriage Counselling",
    counselling_desc:
      "One-to-one confidential sessions grounded in the Qur'ān and Sunnah — for engaged couples, married partners, or families navigating difficulty. Video or in-person.",
    counselling_book_wa_msg:
      "Assalāmu ʿalaykum, I would like to book an Islamic Marriage Counselling session at Islamic Cure & Care.",
    nikah_title: "Islamic Nikah — Marriage Certificate",
    nikah_desc:
      "A full Sharī'ah-compliant Nikah ceremony officiated by a qualified ma'dhūn — includes the signed & stamped marriage certificate, khuṭbah, and witnesses. In-clinic or at your venue.",
    nikah_book_wa_msg:
      "Assalāmu ʿalaykum, I would like to arrange an Islamic Nikah at Islamic Cure & Care.",
    diag_intro:
      "Peace be upon you, and the mercy and blessings of Allāh. For accurate diagnosis via Qur'ān, Prophetic Medicine, herbs and alternative medicine, please answer the following questions honestly.",
    diag_privacy_note: "Your answers will be sent privately to our WhatsApp — no public visibility.",
    diag_name_ph: "Your full name *",
    diag_phone_ph: "Phone (optional)",
    diag_email_ph: "Email (optional)",
    diag_notes_label: "Other notes — recurring dreams, illnesses, comments:",
    diag_notes_ph:
      "Please write anything else you'd like the practitioner to know — including epilepsy, autism, cancer, recurring dreams, etc.",
    diag_send: "Send to clinic via WhatsApp",
    diag_sending: "Sending...",
    diag_consent:
      "By submitting you allow Islamic Cure & Care to use these answers solely for spiritual & medical diagnosis. We never share your data with third parties.",
    diag_thanks: "Submitted — JazākAllāhu khayrā",
    diag_thanks_sub:
      "Your diagnostic summary has been sent to our WhatsApp. If WhatsApp didn't open automatically, click below.",
    diag_open_wa: "Open WhatsApp",
    diag_ans_yes: "Yes",
    diag_ans_no: "No",
    diag_ans_sometimes: "Sometimes",

    // Language
    lang_toggle: "العربية",
    lang_label_en: "English",
    lang_label_ar: "العربية",
    lang_label_ur: "اردو",
    lang_label_de: "Deutsch",
    lang_label_fr: "Français",
  },

  ar: {
    // Header
    home: "الرئيسية",
    ai_search: "البحث الذكي",
    hijama_atlas: "أطلس الحجامة",
    services: "الخدمات",
    ruqyah: "مكتبة الرقية",
    academy: "أكاديمية القرآن والعربية",
    sign_in: "تسجيل الدخول",
    sign_out: "تسجيل الخروج",
    products: "المنتجات",
    contact: "اتصل بنا",
    about: "حول التطبيق",
    tagline: "الطب النبوي · ٢٠٢٦",

    // Disclaimer
    disclaimer_label: "إشعار طبي وقانوني",
    disclaimer_title: "أمانة في يديك",
    disclaimer_intro_1: "العلاج والرعاية الإسلامية ٢٠٢٦",
    disclaimer_intro_2:
      "هو مرجع علمي يجمع بين الطب النبوي الأصيل والأبحاث الحديثة الموثقة. وهو مقدّم",
    disclaimer_intro_3: "للأغراض التعليمية والإرشادية فقط",
    disclaimer_point_1_a: "هذا",
    disclaimer_point_1_b: "ليس بديلاً",
    disclaimer_point_1_c: "عن التشخيص أو العلاج الطبي المتخصص.",
    disclaimer_point_2_a: "استشر دائماً",
    disclaimer_point_2_b: "طبيباً مؤهلاً",
    disclaimer_point_2_c:
      "قبل تطبيق أي علاج، خاصةً أثناء الحمل أو الأمراض المزمنة أو عند تناول الأدوية.",
    disclaimer_point_3_a: "الحجامة يجب أن يقوم بها",
    disclaimer_point_3_b: "ممارس معتمد",
    disclaimer_point_3_c: " فقط.",
    disclaimer_point_4: "نستشهد فقط بالأحاديث الصحيحة والآيات القرآنية الموثقة.",
    disclaimer_accept: "أُقرّ وأقبل — الدخول إلى المكتبة",
    disclaimer_footnote: "بمتابعتك، تُقرّ بأنك قرأت هذا الإشعار وقبلته.",

    // Home
    hero_title_1: "شفاء النبي ﷺ القديم،",
    hero_title_2: "تؤكده العلوم الحديثة.",
    hero_subtitle:
      "أرشيف علمي للعلاجات النبوية الأصيلة — كل مدخل مستمد من حديث صحيح ومدعوم بأبحاث طبية حديثة من ٢٠١٧ إلى ٢٠٢٦.",
    cta_ai: "اسأل الباحث الذكي",
    cta_browse: "تصفّح المكتبة",
    stat_illnesses: "أمراض",
    stat_hadiths: "أحاديث صحيحة",
    stat_points: "نقاط حجامة",
    stat_studies: "دراسات حديثة",

    library_label: "المكتبة",
    library_title: "تصفّح حسب الفئة — من الأمراض المزمنة إلى الروحية.",

    pharmacy_label: "الصيدلية النبوية",
    pharmacy_title: "ثلاثة علاجات. أربعة عشر قرناً من التراث.",
    pharmacy_subtitle:
      "من القرآن والسنة — تحضيرات وثّقها ابن القيم وابن سينا.",

    ai_cta_pill: "بحث الأعراض بالذكاء الاصطناعي",
    ai_cta_title:
      "صف ما تشعر به — واحصل على إرشاد من القرآن والسنة والطب الحديث.",
    ai_cta_subtitle:
      "يبحث المساعد في كامل قاعدة البيانات ليعرض لك الآيات والأحاديث الصحيحة وخرائط الحجامة والعلاجات النبوية والدراسات الحديثة.",
    ai_cta_btn: "ابدأ البحث",

    // Category page
    all_categories: "كل الفئات",
    read_full: "اقرأ المدخل كاملاً",
    no_illness_in_category: "لا توجد أمراض في هذه الفئة بعد.",

    // Illness detail
    back_to_category: "العودة إلى الفئة",
    page_disclaimer:
      "تنبيه طبي: هذا المدخل للأغراض التعليمية فقط. استشر دائماً طبيباً مؤهلاً قبل تطبيق أي علاج أو الخضوع للحجامة. النساء الحوامل والأطفال ومرضى الحالات المزمنة يحتاجون إرشاداً متخصصاً.",
    section_revelation: "النصوص الشرعية",
    section_hijama_map: "خريطة الحجامة",
    section_pharmacy: "الصيدلية النبوية",
    section_lifestyle: "أسلوب حياة النبوي",
    section_studies: "أدلة سريرية ٢٠٢٦",
    studies_subtitle: "· أبحاث حديثة محكّمة",
    label_quran: "القرآن",
    label_sahih_hadith: "حديث صحيح",
    label_preparation: "التحضير:",
    label_duration: "المدة:",
    label_source: "المصدر:",
    label_narrator: "الراوي:",
    sunnah_note: "يشير إلى نقطة موثقة من السنة",

    // Search page
    search_pill: "مساعد البحث الذكي · Claude Sonnet 4.5",
    search_title: "صف أعراضك.",
    search_subtitle:
      "يبحث المساعد في الأحاديث الصحيحة والقرآن وخرائط الحجامة والعلاجات النبوية والدراسات الحديثة في قاعدة بياناتنا — ويعرض المداخل ذات الصلة.",
    search_placeholder: "مثال: أعاني من ألم في المعدة وانتفاخ بعد الأكل…",
    search_btn: "بحث",
    searching: "جارٍ البحث",
    try_these: "جرّب أحد هذه",
    researching: "جارٍ البحث في قاعدة البيانات…",
    researcher_note: "ملاحظة الباحث",
    suggested_entries: "مداخل مقترحة من المكتبة",
    no_results: "لم يُعثر على تطابقات مباشرة. حاول إعادة الصياغة أو تصفّح الفئات من الصفحة الرئيسية.",
    search_failed: "فشل البحث. حاول مرة أخرى.",

    // Hijama Atlas
    atlas_pill: "٢٠٠ نقطة موثقة",
    atlas_title: "أطلس الحجامة",
    atlas_subtitle_1: "خريطة كلاسيكية لنقاط الحجامة مجمّعة من كتاب",
    atlas_subtitle_2: "الطب النبوي",
    atlas_subtitle_3: "لابن القيم، و",
    atlas_subtitle_4: "القانون في الطب",
    atlas_subtitle_5: "لابن سينا، وأطالس الحجامة الحديثة. النقاط الموثّقة من السنة مميّزة بنجمة ذهبية.",
    sunnah_only: "نقاط السنة فقط",
    atlas_search_placeholder: "ابحث بالاسم أو المنطقة أو الاستعمال…",
    points_of: "من",
    points: "نقطة",
    no_points_match: "لا توجد نقاط تطابق التصفية.",
    region: "المنطقة:",
    location: "الموقع:",

    // About
    method_label: "منهجنا",
    about_title: "مكتبة على أعلى معايير الدقّة العلمية.",
    about_intro:
      "العلاج والرعاية الإسلامية ٢٠٢٦ أرشيف حيّ يجمع بين الطب النبوي الأصيل والأدلة الحديثة المحكّمة. فيما يلي المنهجية وراء كل مدخل.",
    about_s1_t: "مصادر موثّقة فقط",
    about_s1_b:
      "كل حديث تم التحقق من صحته في صحيح البخاري، صحيح مسلم، سنن ابن ماجه، سنن أبي داود، وجامع الترمذي. لا ندرج الأحاديث الضعيفة أو الموضوعة. الآيات القرآنية تُذكر مع رقم السورة والآية كاملاً.",
    about_s2_t: "الصيدلية الكلاسيكية",
    about_s2_b:
      "طرق التحضير مأخوذة من كتاب الطب النبوي لابن القيم والقانون في الطب لابن سينا — النصوص المؤسِّسة للطب الإسلامي على مدى أكثر من ألف سنة.",
    about_s3_t: "إسناد سريري حديث",
    about_s3_b:
      "حيث ينطبق، كل علاج مدعوم بدراسة حديثة محكّمة — مع ذكر المجلة والمؤلفين والسنة ورقم DOI — من PubMed وNCBI ومستودعات الأبحاث الجامعية.",
    about_s4_t: "التزام طبي وقانوني صارم",
    about_s4_b:
      "هذا مرجع تعليمي، وليس ممارسة طبية. نافذة التنبيه الإلزامية مقصودة. الحجامة يجب أن يجريها فقط ممارس معتمد. استشر دائماً طبيباً مؤهلاً.",
    about_verse_en: '"وَإِذَا مَرِضْتُ فَهُوَ يَشْفِينِ" — القرآن ٢٦:٨٠',

    // Footer
    footer_intro:
      "اتحاد علمي بين الطب النبوي والأبحاث الحديثة القائمة على الأدلة. كل علاج مستمد من حديث صحيح ومُتحقق منه بدراسات محكّمة.",
    footer_explore: "استكشف",
    footer_disclaimer_title: "تنبيه طبي",
    footer_disclaimer_body_1: "المحتوى مقدّم",
    footer_disclaimer_body_2: "للأغراض التعليمية فقط",
    footer_disclaimer_body_3:
      ". استشر دائماً مختصاً في الرعاية الصحية قبل تطبيق أي علاج. يجب أن تتم الحجامة على يد ممارس معتمد.",
    footer_copyright: "© ٢٠٢٦ العلاج والرعاية الإسلامية · صُمّم بتقديس",
    footer_made_with: "صُنع بـ",
    footer_for_ummah: "للأمة",

    // Body Diagram
    diagram_label: "مرجع بصري",
    diagram_title: "خريطة نقاط الحجامة",
    print_protocol: "طباعة البروتوكول",
    front_view: "المنظر الأمامي",
    back_view: "المنظر الخلفي",
    hover_hint: "حرّك الفأرة فوق نقطة لرؤية اسمها.",
    legend_treatment: "نقطة علاجية",
    legend_sunnah: "نقطة من السنة",
    recommended_points: "النقاط الموصى بها لهذا المرض",
    diagram_disclaimer:
      "★ موثّق في الحديث الصحيح. المواضع التشريحية تقريبية — استعن دائماً بممارس حجامة معتمد.",

    // Ruqyah recommendation
    ruqyah_rec_title: "رقية موصى بها للشفاء",
    ruqyah_rec_subtitle:
      "تسجيلات أصيلة للرقية الشرعية من عيادتنا — استمع إليها بدعاء صادق إلى جانب العلاجات النبوية أدناه.",

    // Today calendar
    today_label: "اليوم",

    // Install App prompt
    install_app_pill: "وفّر وقتك",
    install_app_title: "أضف Islamic Cure & Care إلى شاشتك الرئيسية",
    install_app_cta: "تثبيت",
    install_ios_title: "التثبيت على iPhone",
    install_ios_step1_a: "اضغط على زر",
    install_ios_step1_b: "المشاركة أسفل Safari",
    install_ios_step2: "انزل للأسفل واضغط \"إضافة إلى الشاشة الرئيسية\"",
    install_ios_step3: "اضغط \"إضافة\" — ستظهر أيقونة الأقصى على شاشتك الرئيسية",
    install_ios_close: "تم، جزاك الله خيرًا",

    // Illness page extras
    illness_not_found: "لم يتم العثور على المرض.",
    label_surah: "سورة",
    classical_sources_label: "المصادر الطبية الكلاسيكية",
    source_label: "المصدر",
    atlas_page_label: "صفحة الأطلس",
    share_on_whatsapp: "شارك عبر واتساب",
    share_label: "مشاركة",
    share_copy_link: "نسخ الرابط",
    share_copied: "تم نسخ الرابط!",
    share_ig_copied: "تم النسخ — الصقه في إنستغرام",
    add_to_calendar: "أضف إلى تقويمي",
    add_to_calendar_note: "اختياري — احصل على تذكير على هاتفك قبل كل جلسة.",

    // Service condition bullets
    counselling_includes_1: "جلسة خاصة من 45 إلى 60 دقيقة",
    counselling_includes_2: "عبر الفيديو (Zoom / واتساب) أو حضوريًا",
    counselling_includes_3: "سرية تامة — لا يُشارَك أي شيء",
    counselling_includes_4: "توجيه مستند إلى القرآن والسنة",
    nikah_includes_1: "مأذون مؤهَّل يُجري مراسم العقد",
    nikah_includes_2: "شهادة زواج موقَّعة ومختومة",
    nikah_includes_3: "خطبة النكاح + شاهدان معتمدان",
    nikah_includes_4: "في العيادة أو في مكانك أو مختلط",
    whats_included: "ما يشمله",

    // Online Ruqyah Group
    orgroup_pill: "جديد · كل يوم سبت",
    orgroup_title: "مجموعة الرقية عبر الإنترنت — كل سبت",
    orgroup_subtitle:
      "جلستان أسبوعيتان للرقية الجماعية الشرعية تُقدَّم من العيادة — انضم من أي مكان في العالم.",
    orgroup_women_pill: "الجلسة الأولى",
    orgroup_women_title: "جلسة للنساء فقط",
    orgroup_women_time: "كل سبت · الساعة 4:00 مساءً بتوقيت المملكة المتحدة",
    orgroup_women_wa:
      "السلام عليكم، أود التسجيل في مجموعة الرقية للنساء عبر الإنترنت (كل سبت 4 مساءً بتوقيت المملكة المتحدة).",
    orgroup_men_pill: "الجلسة الثانية",
    orgroup_men_title: "جلسة للرجال فقط",
    orgroup_men_time: "كل سبت · الساعة 6:00 مساءً بتوقيت المملكة المتحدة",
    orgroup_men_wa:
      "السلام عليكم، أود التسجيل في مجموعة الرقية للرجال عبر الإنترنت (كل سبت 6 مساءً بتوقيت المملكة المتحدة).",
    orgroup_platform: "عبر مكالمة فيديو واتساب · Zoom · أو Teams",
    orgroup_per_person: "للشخص، لكل جلسة",
    orgroup_book_cta: "سجّل عبر واتساب",
    orgroup_register_notice:
      "الأماكن محدودة — يُرجى التسجيل قبل 2–3 أيام عبر واتساب حتى نتمكن من تأكيد مكانك وإرسال رابط الاجتماع. التسجيل عبر واتساب فقط (اتصال أو رسالة).",

    // Hijama Sunnah Days card
    sunnah_days_pill: "أيام الحجامة السنية القادمة",
    sunnah_days_hadith:
      "«من أراد أن يحتجم فليلتمس يوم سبعة عشر وتسعة عشر وإحدى وعشرين من الشهر الهجري…»",
    sunnah_days_hadith_ref: "— صحيح سنن ابن ماجه 3486",
    book_on_whatsapp: "احجز عبر واتساب",
    copy_reminder: "نسخ التذكير",
    copied: "✓ تم النسخ!",
    male_female_therapists: "معالجون رجال ونساء",

    // Ruqyah programmes
    ruqyah_programmes_pill: "برامج العلاج الذاتي",
    ruqyah_programmes_title: "برامج الرقية الإضافية",
    ruqyah_programmes_subtitle:
      "ثلاثة بروتوكولات تقليدية من العيادة للعلاج الذاتي في المنزل. اقرنها دائمًا بالدعاء الصادق والتوكل على الله الشافي وحده.",
    section_ingredients: "المكونات",
    section_recite_each: "اقرأ كلًا منها 7 مرات على الخليط",
    section_method: "الطريقة",
    section_additional_step: "خطوة إضافية",
    section_caution: "⚠️ تنبيه",
    ask_on_whatsapp: "اسأل عن هذا عبر واتساب",

    // Diagnostic CTA
    diagnostic_cta_pill: "تشخيص روحي مجاني",
    diagnostic_cta_title: "املأ استبيان الرقية التشخيصي",
    diagnostic_cta_subtitle:
      "أجب عن 20 سؤالًا قصيرًا حول أعراضك — وسنرسل الملخص مباشرة إلى واتساب العيادة لإرشادك بشكل شخصي.",
    diagnostic_cta_btn: "ابدأ الاستبيان",

    // Diagnostic page
    diag_back: "العودة إلى مكتبة الرقية",
    diag_title: "استبيان الرقية التشخيصي",
    diag_gender_label: "الجنس *",
    diag_gender_male: "ذكر",
    diag_gender_female: "أنثى",

    // Islamic Marriage services
    marriage_services_pill: "خدمات الزواج الإسلامي",
    marriage_services_title: "الزواج الإسلامي — الاستشارات وعقد النكاح",
    marriage_services_subtitle:
      "خدمتان شرعيتان تقدّمهما عيادتنا — احجز فورًا عبر واتساب.",
    counselling_title: "استشارات الزواج الإسلامية",
    counselling_desc:
      "جلسات فردية سرية مبنية على القرآن والسنة — للمخطوبين والمتزوجين والعائلات التي تواجه صعوبات. عبر الفيديو أو حضوريًا.",
    counselling_book_wa_msg:
      "السلام عليكم، أود حجز جلسة استشارات زواج إسلامية في عيادة العلاج والرعاية الإسلامية.",
    nikah_title: "النكاح الإسلامي — عقد الزواج",
    nikah_desc:
      "مراسم عقد نكاح شرعية كاملة يُجريها مأذون مؤهَّل — تشمل شهادة الزواج الموقَّعة والمختومة، والخطبة، والشهود. في العيادة أو في مكانكم.",
    nikah_book_wa_msg:
      "السلام عليكم، أود ترتيب عقد نكاح إسلامي في عيادة العلاج والرعاية الإسلامية.",
    diag_intro:
      "السلام عليكم ورحمة الله وبركاته. لتشخيص دقيق عبر القرآن والطب النبوي والأعشاب والطب البديل، يرجى الإجابة عن الأسئلة التالية بصدق.",
    diag_privacy_note: "ستُرسل إجاباتك بشكل خاص إلى واتساب العيادة — دون أي عرض عام.",
    diag_name_ph: "اسمك الكامل *",
    diag_phone_ph: "الهاتف (اختياري)",
    diag_email_ph: "البريد الإلكتروني (اختياري)",
    diag_notes_label: "ملاحظات أخرى — أحلام متكررة، أمراض، تعليقات:",
    diag_notes_ph:
      "اكتب أي شيء آخر تودّ إخبار المعالج به — بما في ذلك الصرع، التوحد، السرطان، الأحلام المتكررة وغيرها.",
    diag_send: "إرسال إلى العيادة عبر واتساب",
    diag_sending: "جارٍ الإرسال...",
    diag_consent:
      "بإرسال هذا الاستبيان، أنت تأذن لـ Islamic Cure & Care باستخدام هذه الإجابات لأغراض التشخيص الروحي والطبي فقط. ولا نشارك بياناتك مع أي طرف ثالث.",
    diag_thanks: "تم الإرسال — جزاك الله خيرًا",
    diag_thanks_sub:
      "تم إرسال ملخص التشخيص إلى واتساب العيادة. إذا لم يُفتح واتساب تلقائيًا، انقر بالأسفل.",
    diag_open_wa: "افتح واتساب",
    diag_ans_yes: "نعم",
    diag_ans_no: "لا",
    diag_ans_sometimes: "أحيانًا",

    // Language
    lang_toggle: "English",
    lang_label_en: "English",
    lang_label_ar: "العربية",
    lang_label_ur: "اردو",
    lang_label_de: "Deutsch",
    lang_label_fr: "Français",
  },

  ur: {
    // Header
    home: "صفحۂ اوّل",
    ai_search: "اے آئی تلاش",
    hijama_atlas: "حجامہ اطلس",
    services: "خدمات",
    ruqyah: "رقیہ لائبریری",
    academy: "قرآن اور عربی اکیڈمی",
    sign_in: "سائن ان",
    sign_out: "سائن آؤٹ",
    products: "مصنوعات",
    contact: "رابطہ",
    about: "ہمارے بارے میں",
    tagline: "طبِ نبوی · ۲۰۲۶",

    // Disclaimer
    disclaimer_label: "طبی اور قانونی نوٹس",
    disclaimer_title: "آپ کے ہاتھوں میں ایک امانت",
    disclaimer_intro_1: "اسلامی علاج اور دیکھ بھال ۲۰۲۶",
    disclaimer_intro_2:
      "ایک علمی حوالہ ہے جو مستند طبِ نبوی کو جدید تحقیق کے ساتھ ملاتا ہے۔ یہ صرف",
    disclaimer_intro_3: "تعلیمی اور معلوماتی مقاصد کے لیے",
    disclaimer_point_1_a: "یہ",
    disclaimer_point_1_b: "متبادل نہیں",
    disclaimer_point_1_c: "کسی پیشہ ور طبی تشخیص یا علاج کا۔",
    disclaimer_point_2_a: "ہمیشہ",
    disclaimer_point_2_b: "اہل ڈاکٹر",
    disclaimer_point_2_c: "سے مشورہ کریں، خاص طور پر حمل، دائمی بیماری یا دوائی کے دوران۔",
    disclaimer_point_3_a: "حجامہ صرف",
    disclaimer_point_3_b: "تصدیق شدہ ماہر",
    disclaimer_point_3_c: " کے ذریعے کیا جانا چاہیے۔",
    disclaimer_point_4: "صرف صحیح احادیث اور مستند قرآنی آیات کا حوالہ دیا جاتا ہے۔",
    disclaimer_accept: "میں سمجھتا ہوں اور قبول کرتا ہوں — لائبریری میں داخل ہوں",
    disclaimer_footnote: "جاری رکھ کر، آپ تسلیم کرتے ہیں کہ آپ نے یہ نوٹس پڑھا اور قبول کیا ہے۔",

    // Home
    hero_title_1: "نبی ﷺ کی قدیم شفا،",
    hero_title_2: "جدید سائنس سے تصدیق شدہ۔",
    hero_subtitle:
      "مستند طبِ نبوی علاجات کا علمی ذخیرہ — ہر اندراج صحیح حدیث میں جڑا ہوا اور ۲۰۱۷–۲۰۲۶ کی جدید کلینیکل تحقیق سے توثیق شدہ۔",
    cta_ai: "اے آئی محقق سے پوچھیں",
    cta_browse: "لائبریری دیکھیں",
    stat_illnesses: "بیماریاں",
    stat_hadiths: "صحیح احادیث",
    stat_points: "حجامہ پوائنٹس",
    stat_studies: "جدید مطالعات",

    library_label: "لائبریری",
    library_title: "زمرے کے لحاظ سے دیکھیں — دائمی سے روحانی تک۔",

    pharmacy_label: "نبوی فارمیسی",
    pharmacy_title: "تین علاج۔ چودہ صدیوں کی روایت۔",
    pharmacy_subtitle: "قرآن و سنت سے — ابن القیم اور ابن سینا کے تیار کردہ نسخے۔",

    ai_cta_pill: "اے آئی علامات کی تلاش",
    ai_cta_title:
      "اپنی علامات بتائیں — قرآن، سنت اور جدید طب پر مبنی رہنمائی حاصل کریں۔",
    ai_cta_subtitle:
      "ہمارا تحقیقی معاون پوری ڈیٹابیس میں آپ کی علامات کو تلاش کرکے قرآنی آیات، صحیح احادیث، حجامہ نقشے، نبوی علاج اور جدید مطالعات پیش کرتا ہے۔",
    ai_cta_btn: "تلاش شروع کریں",

    // Category page
    all_categories: "تمام زمرے",
    read_full: "مکمل اندراج پڑھیں",
    no_illness_in_category: "اس زمرے میں ابھی کوئی بیماری نہیں۔",

    // Illness detail
    back_to_category: "زمرے پر واپس",
    page_disclaimer:
      "طبی انتباہ: یہ اندراج صرف تعلیمی مقاصد کے لیے ہے۔ کوئی بھی علاج یا حجامہ کرانے سے پہلے ہمیشہ اہل ڈاکٹر سے مشورہ کریں۔",
    section_revelation: "وحی کے شواہد",
    section_hijama_map: "حجامہ نقشہ",
    section_pharmacy: "نبوی فارمیسی",
    section_lifestyle: "نبوی طرزِ زندگی",
    section_studies: "۲۰۲۶ کلینیکل ثبوت",
    studies_subtitle: "· نظرثانی شدہ جدید تحقیق",
    label_quran: "قرآن",
    label_sahih_hadith: "صحیح حدیث",
    label_preparation: "تیاری:",
    label_duration: "مدت:",
    label_source: "ماخذ:",
    label_narrator: "راوی:",
    sunnah_note: "سنت سے ثابت نقطہ کی نشاندہی کرتا ہے",

    // Search page
    search_pill: "اے آئی تحقیقی معاون · Claude Sonnet 4.5",
    search_title: "اپنی علامات بیان کریں۔",
    search_subtitle:
      "معاون ہماری ڈیٹابیس میں صحیح حدیث، قرآن، حجامہ نقشے، نبوی علاج اور جدید مطالعات تلاش کرکے متعلقہ اندراجات پیش کرتا ہے۔",
    search_placeholder: "مثال: کھانے کے بعد پیٹ میں درد اور پھولنا…",
    search_btn: "تلاش",
    searching: "تلاش جاری ہے",
    try_these: "ان میں سے کوئی آزمائیں",
    researching: "ڈیٹابیس میں تحقیق جاری ہے…",
    researcher_note: "محقق کا نوٹ",
    suggested_entries: "لائبریری سے تجویز کردہ اندراجات",
    no_results: "براہ راست کوئی نتیجہ نہیں ملا۔ دوبارہ کوشش کریں یا زمرے دیکھیں۔",
    search_failed: "تلاش ناکام ہوئی۔ دوبارہ کوشش کریں۔",

    // Hijama Atlas
    atlas_pill: "۲۰۰ مستند پوائنٹس",
    atlas_title: "حجامہ اطلس",
    atlas_subtitle_1: "حجامہ نقاط کا کلاسیکی نقشہ، ابن القیم کی",
    atlas_subtitle_2: "الطب النبوی",
    atlas_subtitle_3: "، ابن سینا کی",
    atlas_subtitle_4: "القانون في الطب",
    atlas_subtitle_5: "، اور جدید حجامہ اطالس سے مرتب۔ سنت کے پوائنٹس کو سنہری ستارے سے ممتاز کیا گیا ہے۔",
    sunnah_only: "صرف سنت پوائنٹس",
    atlas_search_placeholder: "نام، علاقہ یا استعمال کے لحاظ سے تلاش کریں…",
    points_of: "میں سے",
    points: "پوائنٹس",
    no_points_match: "کوئی پوائنٹ آپ کی فلٹرنگ سے میل نہیں کھاتا۔",
    region: "علاقہ:",
    location: "مقام:",

    // About
    method_label: "ہمارا طریقہ",
    about_title: "اعلیٰ علمی معیار کی لائبریری۔",
    about_intro:
      "اسلامی علاج اور دیکھ بھال ۲۰۲۶ ایک زندہ ذخیرہ ہے جو مستند طبِ نبوی کو جدید نظرثانی شدہ شواہد کے ساتھ ملاتا ہے۔",
    about_s1_t: "صرف مستند ذرائع",
    about_s1_b:
      "ہر حدیث صحیح بخاری، مسلم، ابن ماجہ، ابو داؤد اور ترمذی سے تصدیق شدہ ہے۔ ضعیف یا موضوع روایات شامل نہیں۔",
    about_s2_t: "کلاسیکی فارمیسی",
    about_s2_b:
      "تیاری کے طریقے ابن القیم کی 'الطب النبوی' اور ابن سینا کی 'القانون في الطب' سے لیے گئے ہیں۔",
    about_s3_t: "جدید کلینیکل توثیق",
    about_s3_b:
      "جہاں قابلِ اطلاق ہو، ہر علاج جدید نظرثانی شدہ مطالعہ سے تائید شدہ ہے — PubMed اور یونیورسٹی تحقیقی ذخائر سے۔",
    about_s4_t: "سخت قانونی اور طبی پابندی",
    about_s4_b:
      "یہ ایک تعلیمی حوالہ ہے، طبی پریکٹس نہیں۔ ہمیشہ اہل ڈاکٹر سے مشورہ کریں۔",
    about_verse_en: '"اور جب میں بیمار ہوتا ہوں تو وہی مجھے شفا دیتا ہے۔" — قرآن ۲۶:۸۰',

    // Footer
    footer_intro:
      "طبِ نبوی اور جدید شواہد پر مبنی تحقیق کا علمی اتحاد۔ ہر علاج صحیح حدیث میں جڑا ہوا اور نظرثانی شدہ مطالعات سے توثیق شدہ۔",
    footer_explore: "دیکھیں",
    footer_disclaimer_title: "طبی انتباہ",
    footer_disclaimer_body_1: "مواد فراہم کیا گیا ہے",
    footer_disclaimer_body_2: "صرف تعلیمی مقاصد کے لیے",
    footer_disclaimer_body_3:
      "۔ کوئی بھی علاج کرنے سے پہلے ہمیشہ اہل صحت پیشہ ور سے مشورہ کریں۔ حجامہ صرف تصدیق شدہ ماہر کرے۔",
    footer_copyright: "© ۲۰۲۶ اسلامی علاج اور دیکھ بھال · احترام سے تیار",
    footer_made_with: "بنایا گیا",
    footer_for_ummah: "امّت کے لیے",

    // Body Diagram
    diagram_label: "بصری حوالہ",
    diagram_title: "حجامہ پوائنٹ نقشہ",
    print_protocol: "پروٹوکول پرنٹ کریں",
    front_view: "سامنے کا منظر",
    back_view: "پیچھے کا منظر",
    hover_hint: "نقطہ پر ماؤس لے جائیں تاکہ نام دیکھ سکیں۔",
    legend_treatment: "علاج کا نقطہ",
    legend_sunnah: "سنت کا نقطہ",
    recommended_points: "اس بیماری کے لیے تجویز کردہ پوائنٹس",
    diagram_disclaimer:
      "★ صحیح حدیث میں ثابت۔ تشریحی مقامات تقریبی ہیں — ہمیشہ تصدیق شدہ حجامہ ماہر سے رجوع کریں۔",

    // Ruqyah recommendation
    ruqyah_rec_title: "آپ کی شفا کے لیے تجویز کردہ رقیہ",
    ruqyah_rec_subtitle:
      "ہماری کلینک سے مستند قرآنی رقیہ — مخلصانہ دعا کے ساتھ نبوی علاج کے ہمراہ سنیں۔",

    // Today calendar
    today_label: "آج",

    // Install App prompt
    install_app_pill: "وقت بچائیں",
    install_app_title: "Islamic Cure & Care کو ہوم اسکرین پر شامل کریں",
    install_app_cta: "انسٹال کریں",
    install_ios_title: "iPhone پر انسٹال کریں",
    install_ios_step1_a: "Safari کے نیچے",
    install_ios_step1_b: "شیئر بٹن پر ٹیپ کریں",
    install_ios_step2: "نیچے اسکرول کریں اور \"Add to Home Screen\" پر ٹیپ کریں",
    install_ios_step3: "\"Add\" پر ٹیپ کریں — اقصیٰ کا آئیکن آپ کی ہوم اسکرین پر ظاہر ہو گا",
    install_ios_close: "ٹھیک ہے، جزاک اللہ",

    // Illness page extras
    illness_not_found: "بیماری نہیں ملی۔",
    label_surah: "سورہ",
    classical_sources_label: "کلاسیکی طبی ماخذ",
    source_label: "ماخذ",
    atlas_page_label: "اطلس صفحہ",
    share_on_whatsapp: "واٹس ایپ پر شیئر کریں",
    share_label: "شیئر",
    share_copy_link: "لنک کاپی کریں",
    share_copied: "لنک کاپی ہو گیا!",
    share_ig_copied: "کاپی ہو گیا — Instagram میں پیسٹ کریں",
    add_to_calendar: "میرے کیلنڈر میں شامل کریں",
    add_to_calendar_note: "اختیاری — ہر سیشن سے پہلے اپنے فون پر یاد دہانی حاصل کریں۔",

    // Service condition bullets
    counselling_includes_1: "45–60 منٹ کا نجی سیشن",
    counselling_includes_2: "ویڈیو (Zoom / واٹس ایپ) یا حاضر ہو کر",
    counselling_includes_3: "مکمل رازداری — کچھ بھی شیئر نہیں کیا جائے گا",
    counselling_includes_4: "قرآن و سنت پر مبنی رہنمائی",
    nikah_includes_1: "قابل مأذون تقریب کی سرپرستی کرتا ہے",
    nikah_includes_2: "دستخط شدہ اور مہر شدہ سرٹیفکیٹ فراہم کیا جائے گا",
    nikah_includes_3: "خطبہ نکاح + 2 گواہ",
    nikah_includes_4: "کلینک میں، آپ کے مقام پر، یا مخلوط",
    whats_included: "کیا شامل ہے",

    // Online Ruqyah Group
    orgroup_pill: "نیا · ہر ہفتہ",
    orgroup_title: "آن لائن رقیہ گروپ — ہر ہفتہ",
    orgroup_subtitle:
      "ہمارے کلینک کی دو ہفتہ وار شرعی گروپ رقیہ سیشنز — دنیا کے کسی بھی حصے سے شامل ہوں۔",
    orgroup_women_pill: "پہلا سیشن",
    orgroup_women_title: "صرف خواتین کے لیے سیشن",
    orgroup_women_time: "ہر ہفتہ · شام 4:00 UK وقت",
    orgroup_women_wa:
      "السلام علیکم، میں آن لائن خواتین کے رقیہ گروپ (ہر ہفتہ 4 PM UK) میں رجسٹر کرنا چاہتی/چاہتا ہوں۔",
    orgroup_men_pill: "دوسرا سیشن",
    orgroup_men_title: "صرف مردوں کے لیے سیشن",
    orgroup_men_time: "ہر ہفتہ · شام 6:00 UK وقت",
    orgroup_men_wa:
      "السلام علیکم، میں آن لائن مردوں کے رقیہ گروپ (ہر ہفتہ 6 PM UK) میں رجسٹر کرنا چاہتا ہوں۔",
    orgroup_platform: "واٹس ایپ ویڈیو کال · Zoom · یا Teams کے ذریعے",
    orgroup_per_person: "فی شخص، فی سیشن",
    orgroup_book_cta: "واٹس ایپ پر رجسٹر کریں",
    orgroup_register_notice:
      "جگہیں محدود ہیں — براہِ کرم 2–3 دن پہلے واٹس ایپ کے ذریعے رجسٹر کریں تاکہ ہم آپ کی جگہ تصدیق کر سکیں اور میٹنگ لنک بھیج سکیں۔ رجسٹریشن صرف واٹس ایپ کے ذریعے (کال یا میسج)۔",

    // Hijama Sunnah Days card
    sunnah_days_pill: "آنے والے حجامہ سنت دن",
    sunnah_days_hadith:
      "''جو حجامہ کرنا چاہے، وہ ہجری مہینے کی 17، 19 اور 21 تاریخ کو تلاش کرے…''",
    sunnah_days_hadith_ref: "— صحیح سنن ابن ماجہ 3486",
    book_on_whatsapp: "واٹس ایپ پر بکنگ کریں",
    copy_reminder: "یاد دہانی کاپی کریں",
    copied: "✓ کاپی ہو گیا!",
    male_female_therapists: "مرد و خواتین معالج دستیاب",

    // Ruqyah programmes
    ruqyah_programmes_pill: "خود علاج کے پروگرام",
    ruqyah_programmes_title: "اضافی رقیہ پروگرام",
    ruqyah_programmes_subtitle:
      "گھر پر خود علاج کے لیے کلینک کے فراہم کردہ تین روایتی پروٹوکول۔ ہمیشہ مخلصانہ دعا اور اللہ پر بھروسے کے ساتھ شامل کریں، جو اکیلا حقیقی شفا دینے والا ہے۔",
    section_ingredients: "اجزاء",
    section_recite_each: "ہر ایک کو مرکب پر 7 بار پڑھیں",
    section_method: "طریقہ",
    section_additional_step: "اضافی قدم",
    section_caution: "⚠️ احتیاط",
    ask_on_whatsapp: "اس بارے میں واٹس ایپ پر پوچھیں",

    // Diagnostic CTA
    diagnostic_cta_pill: "مفت روحانی تشخیص",
    diagnostic_cta_title: "رقیہ تشخیصی سوالنامہ پُر کریں",
    diagnostic_cta_subtitle:
      "اپنی علامات کے بارے میں 20 مختصر سوالات کا جواب دیں — ہم خلاصہ براہِ راست ہمارے واٹس ایپ پر بھیجیں گے تاکہ ذاتی رہنمائی کی جا سکے۔",
    diagnostic_cta_btn: "سوالنامہ شروع کریں",

    // Diagnostic page
    diag_back: "رقیہ لائبریری پر واپس",
    diag_title: "رقیہ تشخیصی سوالنامہ",
    diag_gender_label: "جنس *",
    diag_gender_male: "مرد",
    diag_gender_female: "عورت",

    // Islamic Marriage services
    marriage_services_pill: "اسلامی شادی کی خدمات",
    marriage_services_title: "اسلامی شادی — مشاورت اور نکاح",
    marriage_services_subtitle:
      "ہمارے کلینک کی دو شرعی خدمات — واٹس ایپ پر فوراً بکنگ کریں۔",
    counselling_title: "اسلامی شادی کی مشاورت",
    counselling_desc:
      "قرآن و سنت پر مبنی ذاتی، خفیہ سیشنز — منگنی شدہ جوڑوں، شادی شدہ ساتھیوں، یا مشکلات کا سامنا کرنے والے خاندانوں کے لیے۔ ویڈیو یا ذاتی طور پر۔",
    counselling_book_wa_msg:
      "السلام علیکم، میں Islamic Cure & Care میں اسلامی شادی کی مشاورت کا سیشن بک کرنا چاہتا/چاہتی ہوں۔",
    nikah_title: "اسلامی نکاح — شادی کا سرٹیفکیٹ",
    nikah_desc:
      "مکمل شرعی نکاح تقریب جو ایک قابل مأذون کے ذریعے کی جاتی ہے — دستخط شدہ اور مہر شدہ شادی کا سرٹیفکیٹ، خطبہ اور گواہ شامل ہیں۔ کلینک میں یا آپ کے مقام پر۔",
    nikah_book_wa_msg:
      "السلام علیکم، میں Islamic Cure & Care میں اسلامی نکاح کا انتظام کرنا چاہتا/چاہتی ہوں۔",
    diag_intro:
      "السلام علیکم ورحمۃ اللہ وبرکاتہ۔ قرآن، نبوی طب، جڑی بوٹیوں اور متبادل طب کے ذریعے درست تشخیص کے لیے، براہِ کرم ذیل کے سوالات کا ایمانداری سے جواب دیں۔",
    diag_privacy_note: "آپ کے جوابات ہمارے واٹس ایپ پر نجی طور پر بھیجے جائیں گے — کوئی عوامی نمائش نہیں۔",
    diag_name_ph: "آپ کا مکمل نام *",
    diag_phone_ph: "فون (اختیاری)",
    diag_email_ph: "ای میل (اختیاری)",
    diag_notes_label: "دیگر نوٹس — تکراری خواب، بیماریاں، تبصرے:",
    diag_notes_ph:
      "براہِ کرم کوئی بھی اضافی بات لکھیں جو معالج کو جاننی چاہیے — بشمول مرگی، آٹزم، کینسر، تکراری خواب وغیرہ۔",
    diag_send: "کلینک کو واٹس ایپ پر بھیجیں",
    diag_sending: "بھیجا جا رہا ہے...",
    diag_consent:
      "بھیجنے سے، آپ Islamic Cure & Care کو ان جوابات کو صرف روحانی اور طبی تشخیص کے لیے استعمال کرنے کی اجازت دیتے ہیں۔ ہم آپ کا ڈیٹا کسی فریقِ ثالث سے شیئر نہیں کرتے۔",
    diag_thanks: "بھیج دیا گیا — جزاک اللہ خیراً",
    diag_thanks_sub:
      "آپ کی تشخیصی خلاصہ ہمارے واٹس ایپ پر بھیج دیا گیا۔ اگر واٹس ایپ خودکار نہیں کھلا تو نیچے کلک کریں۔",
    diag_open_wa: "واٹس ایپ کھولیں",
    diag_ans_yes: "ہاں",
    diag_ans_no: "نہیں",
    diag_ans_sometimes: "کبھی کبھار",

    // Language
    lang_toggle: "English",
    lang_label_en: "English",
    lang_label_ar: "العربية",
    lang_label_ur: "اردو",
    lang_label_de: "Deutsch",
    lang_label_fr: "Français",
  },

  de: {
    // Header
    home: "Startseite",
    ai_search: "KI-Suche",
    hijama_atlas: "Hijama-Atlas",
    services: "Leistungen",
    ruqyah: "Ruqyah-Bibliothek",
    academy: "Koran- & Arabisch-Akademie",
    sign_in: "Anmelden",
    sign_out: "Abmelden",
    products: "Produkte",
    contact: "Kontakt",
    about: "Über uns",
    tagline: "Tibb al-Nabawi · 2026",

    // Disclaimer
    disclaimer_label: "Medizinischer & Rechtlicher Hinweis",
    disclaimer_title: "Ein Vertrauen, das in Ihre Hände gelegt wurde",
    disclaimer_intro_1: "Islamic Cure & Care 2026",
    disclaimer_intro_2: "ist eine wissenschaftliche Referenz, die authentische Tibb al-Nabawi (prophetische Medizin) mit begutachteten modernen Studien verbindet. Sie dient",
    disclaimer_intro_3: "ausschließlich zu Bildungs- und Informationszwecken",
    disclaimer_point_1_a: "Dies ist",
    disclaimer_point_1_b: "kein Ersatz",
    disclaimer_point_1_c: "für eine professionelle medizinische Diagnose oder Behandlung.",
    disclaimer_point_2_a: "Konsultieren Sie stets einen",
    disclaimer_point_2_b: "qualifizierten Arzt",
    disclaimer_point_2_c: "vor der Anwendung eines Heilmittels, insbesondere während der Schwangerschaft, bei chronischen Erkrankungen oder bei Einnahme von Medikamenten.",
    disclaimer_point_3_a: "Hijama (Schröpfen) darf nur von einem",
    disclaimer_point_3_b: "zertifizierten Therapeuten",
    disclaimer_point_3_c: "durchgeführt werden.",
    disclaimer_point_4: "Es werden ausschließlich authentische (Sahih) Hadithe und überprüfte Koranverse zitiert.",
    disclaimer_accept: "Ich verstehe und akzeptiere — Bibliothek betreten",
    disclaimer_footnote: "Mit dem Fortfahren bestätigen Sie, diesen Hinweis gelesen und akzeptiert zu haben.",

    // Home
    hero_title_1: "Die alte Heilkunst des Propheten ﷺ,",
    hero_title_2: "von der modernen Wissenschaft bestätigt.",
    hero_subtitle: "Ein wissenschaftliches Archiv authentischer Tibb-al-Nabawi-Heilmittel — jeder Eintrag ist in Sahih-Hadithen verankert und mit begutachteten klinischen Studien von 2017–2026 verknüpft.",
    cta_ai: "Den KI-Forscher fragen",
    cta_browse: "Bibliothek durchsuchen",
    stat_illnesses: "Krankheiten",
    stat_hadiths: "Sahih-Hadithe",
    stat_points: "Hijama-Punkte",
    stat_studies: "Moderne Studien",

    library_label: "Die Bibliothek",
    library_title: "Erkunden Sie nach Kategorie — vom Chronischen bis zum Spirituellen.",

    pharmacy_label: "Prophetische Apotheke",
    pharmacy_title: "Drei Heilmittel. Vierzehn Jahrhunderte Tradition.",
    pharmacy_subtitle: "Aus dem Koran und der Sunnah — Zubereitungen, dokumentiert von Ibn al-Qayyim und Ibn Sina.",

    ai_cta_pill: "KI-gestützte Symptomsuche",
    ai_cta_title: "Beschreiben Sie, was Sie fühlen — erhalten Sie eine Orientierung, verwurzelt in Koran, Sunnah und moderner Medizin.",
    ai_cta_subtitle: "Unser Forschungsassistent gleicht Ihre Symptome mit der gesamten Datenbank ab und liefert Koranverse, authentische Hadithe, Hijama-Zuordnungen, prophetische Heilmittel und moderne klinische Studien.",
    ai_cta_btn: "Suche starten",

    // Category page
    all_categories: "Alle Kategorien",
    read_full: "Vollständigen Eintrag lesen",
    no_illness_in_category: "Noch keine Krankheiten in dieser Kategorie.",

    // Illness detail
    back_to_category: "Zurück zur Kategorie",
    page_disclaimer: "Medizinischer Hinweis: Dieser Eintrag dient ausschließlich Bildungszwecken. Konsultieren Sie immer einen qualifizierten Arzt, bevor Sie ein Heilmittel anwenden oder eine Hijama-Therapie durchführen lassen. Schwangere, Kinder und Patienten mit chronischen Erkrankungen benötigen fachärztliche Beratung.",
    section_revelation: "Umfassende Offenbarung",
    section_hijama_map: "Hijama-Karte",
    section_pharmacy: "Prophetische Apotheke",
    section_lifestyle: "Sunnah-Lebensweise",
    section_studies: "Klinischer Nachweis 2026",
    studies_subtitle: "· Begutachtete moderne Forschung",
    label_quran: "Koran",
    label_sahih_hadith: "Sahih-Hadith",
    label_preparation: "Zubereitung:",
    label_duration: "Dauer:",
    label_source: "Quelle:",
    label_narrator: "Überlieferer:",
    sunnah_note: "kennzeichnet einen in der Sunnah dokumentierten Punkt",

    // Search
    search_pill: "KI-Forschungsassistent · Claude Sonnet 4.5",
    search_title: "Beschreiben Sie Ihre Symptome.",
    search_subtitle: "Der Assistent durchsucht authentische Hadithe, den Koran, Hijama-Zuordnungen, prophetische Heilmittel und moderne begutachtete Studien in unserer Datenbank — und liefert relevante Einträge.",
    search_placeholder: "z. B. Ich habe Bauchschmerzen und Blähungen nach den Mahlzeiten…",
    search_btn: "Suchen",
    searching: "Suche läuft",
    try_these: "Probieren Sie eines davon",
    researching: "Recherchiere in der Datenbank…",
    researcher_note: "Anmerkung des Forschers",
    suggested_entries: "Vorgeschlagene Einträge aus der Bibliothek",
    no_results: "Keine direkten Treffer gefunden. Versuchen Sie eine andere Formulierung oder durchsuchen Sie die Kategorien von der Startseite.",
    search_failed: "Suche fehlgeschlagen. Bitte versuchen Sie es erneut.",

    // Hijama Atlas
    atlas_pill: "200 dokumentierte Punkte",
    atlas_title: "Der Hijama-Atlas",
    atlas_subtitle_1: "Eine klassische Kartierung der Schröpfpunkte, zusammengestellt aus Ibn al-Qayyims",
    atlas_subtitle_2: "At-Tibb An-Nabawi",
    atlas_subtitle_3: ", Ibn Sinas",
    atlas_subtitle_4: "Al-Qanun fi al-Tibb",
    atlas_subtitle_5: " sowie modernen Hijama-Praxisatlanten. Sunnah-Punkte sind mit einem goldenen Stern markiert.",
    sunnah_only: "Nur Sunnah-Punkte",
    atlas_search_placeholder: "Suche nach Name, Region oder Indikation…",
    points_of: "von",
    points: "Punkten",
    no_points_match: "Keine Punkte entsprechen Ihren Filtern.",
    region: "Region:",
    location: "Lage:",

    // About
    method_label: "Unsere Methode",
    about_title: "Eine Bibliothek, die höchsten wissenschaftlichen Standards entspricht.",
    about_intro: "Islamic Cure & Care 2026 ist ein lebendiges Archiv, das authentische prophetische Medizin (Tibb al-Nabawi) mit modernen begutachteten Erkenntnissen vereint. Nachfolgend die Methodik hinter jedem Eintrag.",
    about_s1_t: "Ausschließlich authentische Quellen",
    about_s1_b: "Jeder Hadith wird als Sahih (authentisch) über Sahih al-Bukhari, Sahih Muslim, Sunan Ibn Majah, Sunan Abi Dawud und Jami at-Tirmidhi verifiziert. Schwache (Da'if) oder erfundene (Mawdu') Überlieferungen werden nicht aufgenommen. Koranverse werden mit vollständiger Sura- und Aya-Referenz zitiert.",
    about_s2_t: "Klassische Pharmazie",
    about_s2_b: "Die Zubereitungsmethoden stammen aus Ibn al-Qayyims At-Tibb An-Nabawi und Ibn Sinas Al-Qanun fi al-Tibb (Der Kanon der Medizin) — den Grundlagentexten der islamischen Medizin seit über 1.000 Jahren.",
    about_s3_t: "Moderner klinischer Abgleich",
    about_s3_b: "Soweit anwendbar, wird jedes Heilmittel durch eine zeitgenössische begutachtete Studie gestützt — mit Angabe von Journal, Autoren, Jahr und DOI — aus PubMed, NCBI und universitären Forschungsarchiven.",
    about_s4_t: "Strenge rechtliche und medizinische Konformität",
    about_s4_b: "Dies ist eine Bildungsreferenz, keine medizinische Praxis. Das Hinweisfenster, das diese Seite vorschaltet, ist beabsichtigt. Hijama (Schröpfen) darf nur von einem zertifizierten Therapeuten durchgeführt werden. Konsultieren Sie stets einen qualifizierten Arzt.",
    about_verse_en: '„Und wenn ich krank bin, ist Er es, der mich heilt." — Koran 26:80',

    // Footer
    footer_intro: "Eine wissenschaftliche Verbindung von Tibb al-Nabawi (prophetischer Medizin) und moderner evidenzbasierter Forschung. Jedes Heilmittel ist in authentischen Sahih-Hadithen verwurzelt und durch begutachtete Studien verifiziert.",
    footer_explore: "Entdecken",
    footer_disclaimer_title: "Medizinischer Hinweis",
    footer_disclaimer_body_1: "Inhalt nur zu",
    footer_disclaimer_body_2: "Bildungszwecken",
    footer_disclaimer_body_3: ". Konsultieren Sie immer eine qualifizierte medizinische Fachkraft, bevor Sie ein Heilmittel anwenden. Hijama (Schröpfen) muss von einem zertifizierten Therapeuten durchgeführt werden.",
    footer_copyright: "© 2026 Islamic Cure & Care · Mit Ehrfurcht erstellt",
    footer_made_with: "Erstellt mit",
    footer_for_ummah: "für die Ummah",

    // Body Diagram
    diagram_label: "Visuelle Referenz",
    diagram_title: "Diagramm der Hijama-Punkte",
    print_protocol: "Protokoll drucken",
    front_view: "Vorderansicht",
    back_view: "Rückansicht",
    hover_hint: "Bewegen Sie den Mauszeiger über einen Punkt, um seinen Namen zu sehen.",
    legend_treatment: "Behandlungspunkt",
    legend_sunnah: "Sunnah-Punkt",
    recommended_points: "Empfohlene Punkte für diese Krankheit",
    diagram_disclaimer: "★ In der Sunnah durch einen authentischen Hadith dokumentiert. Die anatomischen Positionen sind ungefähr — suchen Sie stets einen zertifizierten Hijama-Therapeuten auf.",

    // Ruqyah recommendation
    ruqyah_rec_title: "Empfohlene Ruqyah für Ihre Heilung",
    ruqyah_rec_subtitle: "Authentische koranische Ruqyah-Aufnahmen aus unserer Klinik — hören Sie mit aufrichtigem Bittgebet zusammen mit den unten aufgeführten prophetischen Heilmitteln.",

    // Today calendar
    today_label: "Heute",

    // Install App prompt
    install_app_pill: "Zeit sparen",
    install_app_title: "Islamic Cure & Care zum Startbildschirm hinzufügen",
    install_app_cta: "Installieren",
    install_ios_title: "Auf dem iPhone installieren",
    install_ios_step1_a: "Tippen Sie auf die",
    install_ios_step1_b: "Teilen-Schaltfläche am unteren Rand von Safari",
    install_ios_step2: "Scrollen Sie nach unten und tippen Sie auf \"Zum Home-Bildschirm\"",
    install_ios_step3: "Tippen Sie auf \"Hinzufügen\" — das Al-Aqsa-Symbol erscheint auf Ihrem Startbildschirm",
    install_ios_close: "Verstanden, JazākAllāh",

    // Illness page extras
    illness_not_found: "Krankheit nicht gefunden.",
    label_surah: "Sure",
    classical_sources_label: "Klassische medizinische Quellen",
    source_label: "Quelle",
    atlas_page_label: "Atlas-Seite",
    share_on_whatsapp: "Auf WhatsApp teilen",
    share_label: "Teilen",
    share_copy_link: "Link kopieren",
    share_copied: "Link kopiert!",
    share_ig_copied: "Kopiert — in Instagram einfügen",
    add_to_calendar: "Zu meinem Kalender hinzufügen",
    add_to_calendar_note: "Optional — erhalten Sie eine Erinnerung auf Ihrem Telefon vor jeder Sitzung.",

    // Service condition bullets
    counselling_includes_1: "45–60 Minuten private Sitzung",
    counselling_includes_2: "Per Video (Zoom / WhatsApp) oder persönlich",
    counselling_includes_3: "Strikte Vertraulichkeit — nichts wird geteilt",
    counselling_includes_4: "Beratung auf Grundlage von Qur'ān & Sunnah",
    nikah_includes_1: "Qualifizierter Ma'dhūn leitet die Zeremonie",
    nikah_includes_2: "Unterschriebene & gestempelte Urkunde",
    nikah_includes_3: "Khuṭbah + 2 Zeugen inklusive",
    nikah_includes_4: "In der Klinik, an Ihrem Ort oder hybrid",
    whats_included: "Enthalten",

    // Online Ruqyah Group
    orgroup_pill: "Neu · Jeden Samstag",
    orgroup_title: "Online Ruqyah-Gruppe — Jeden Samstag",
    orgroup_subtitle:
      "Zwei wöchentliche Sharī'ah-konforme Gruppen-Ruqyah-Sitzungen unserer Klinik — nehmen Sie von überall auf der Welt teil.",
    orgroup_women_pill: "Sitzung 1",
    orgroup_women_title: "Nur-Frauen-Sitzung",
    orgroup_women_time: "Jeden Samstag · 16:00 Uhr britische Zeit",
    orgroup_women_wa:
      "Assalāmu ʿalaykum, ich möchte mich für die Online-Ruqyah-Gruppe nur für Frauen (Samstags 16 Uhr britische Zeit) anmelden.",
    orgroup_men_pill: "Sitzung 2",
    orgroup_men_title: "Nur-Männer-Sitzung",
    orgroup_men_time: "Jeden Samstag · 18:00 Uhr britische Zeit",
    orgroup_men_wa:
      "Assalāmu ʿalaykum, ich möchte mich für die Online-Ruqyah-Gruppe nur für Männer (Samstags 18 Uhr britische Zeit) anmelden.",
    orgroup_platform: "Per WhatsApp-Videoanruf · Zoom · oder Teams",
    orgroup_per_person: "pro Person, pro Sitzung",
    orgroup_book_cta: "Über WhatsApp anmelden",
    orgroup_register_notice:
      "Plätze sind begrenzt — bitte melden Sie sich 2–3 Tage im Voraus per WhatsApp an, damit wir Ihren Platz bestätigen und den Meeting-Link senden können. Anmeldung nur per WhatsApp (Anruf oder Nachricht).",

    // Hijama Sunnah Days card
    sunnah_days_pill: "Kommende Hijama-Sunnah-Tage",
    sunnah_days_hadith:
      "„Wer Hijama durchführen möchte, der soll den 17., 19. und 21. Tag des islamischen Mondmonats wählen…“",
    sunnah_days_hadith_ref: "— Ṣaḥīḥ Sunan Ibn Mājah 3486",
    book_on_whatsapp: "Per WhatsApp buchen",
    copy_reminder: "Erinnerung kopieren",
    copied: "✓ Kopiert!",
    male_female_therapists: "männliche & weibliche Therapeuten",

    // Ruqyah programmes
    ruqyah_programmes_pill: "Selbstbehandlungs­programme",
    ruqyah_programmes_title: "Weitere Ruqyah-Programme",
    ruqyah_programmes_subtitle:
      "Drei traditionelle, von der Klinik bereitgestellte Protokolle zur Selbstbehandlung zu Hause. Kombinieren Sie sie stets mit aufrichtigem Duʿā und Vertrauen in Allāh als den einzig wahren Heiler.",
    section_ingredients: "Zutaten",
    section_recite_each: "Jedes 7 Mal über die Mischung rezitieren",
    section_method: "Methode",
    section_additional_step: "Zusätzlicher Schritt",
    section_caution: "⚠️ Vorsicht",
    ask_on_whatsapp: "Per WhatsApp danach fragen",

    // Diagnostic CTA
    diagnostic_cta_pill: "Kostenlose spirituelle Diagnose",
    diagnostic_cta_title: "Den Ruqyah-Diagnose­fragebogen ausfüllen",
    diagnostic_cta_subtitle:
      "Beantworten Sie 20 kurze Fragen zu Ihren Symptomen — wir senden die Zusammenfassung direkt an unser WhatsApp für persönliche Beratung.",
    diagnostic_cta_btn: "Fragebogen starten",

    // Diagnostic page
    diag_back: "Zurück zur Ruqyah-Bibliothek",
    diag_title: "Ruqyah-Diagnose­fragebogen",
    diag_gender_label: "Geschlecht *",
    diag_gender_male: "Männlich",
    diag_gender_female: "Weiblich",

    // Islamic Marriage services
    marriage_services_pill: "Islamische Eheschließungs­dienste",
    marriage_services_title: "Islamische Ehe — Beratung & Nikāh",
    marriage_services_subtitle:
      "Zwei Sharī'ah-konforme Dienstleistungen unserer Klinik — sofort per WhatsApp buchen.",
    counselling_title: "Islamische Eheberatung",
    counselling_desc:
      "Vertrauliche Einzelsitzungen auf Grundlage von Qur'ān und Sunnah — für verlobte Paare, verheiratete Partner oder Familien in schwierigen Situationen. Per Video oder persönlich.",
    counselling_book_wa_msg:
      "Assalāmu ʿalaykum, ich möchte eine islamische Eheberatung bei Islamic Cure & Care buchen.",
    nikah_title: "Islamische Nikāh — Ehe­urkunde",
    nikah_desc:
      "Eine vollständige, Sharī'ah-konforme Nikāh-Zeremonie, durchgeführt von einem qualifizierten Ma'dhūn — inklusive unterschriebener und gestempelter Heiratsurkunde, Khuṭbah und Zeugen. In der Klinik oder an Ihrem Veranstaltungsort.",
    nikah_book_wa_msg:
      "Assalāmu ʿalaykum, ich möchte eine islamische Nikāh bei Islamic Cure & Care arrangieren.",
    diag_intro:
      "Friede sei mit Ihnen, und die Barmherzigkeit und der Segen Allāhs. Für eine genaue Diagnose mittels Qur'ān, prophetischer Medizin, Kräuter und Alternativmedizin beantworten Sie bitte die folgenden Fragen ehrlich.",
    diag_privacy_note:
      "Ihre Antworten werden privat an unser WhatsApp gesendet — keine öffentliche Sichtbarkeit.",
    diag_name_ph: "Ihr vollständiger Name *",
    diag_phone_ph: "Telefon (optional)",
    diag_email_ph: "E-Mail (optional)",
    diag_notes_label: "Weitere Notizen — wiederkehrende Träume, Krankheiten, Kommentare:",
    diag_notes_ph:
      "Schreiben Sie alles, was der Therapeut wissen sollte — einschließlich Epilepsie, Autismus, Krebs, wiederkehrende Träume usw.",
    diag_send: "Über WhatsApp an die Klinik senden",
    diag_sending: "Wird gesendet...",
    diag_consent:
      "Mit dem Absenden erlauben Sie Islamic Cure & Care, diese Antworten ausschließlich für spirituelle und medizinische Diagnosen zu verwenden. Wir geben Ihre Daten niemals an Dritte weiter.",
    diag_thanks: "Gesendet — JazākAllāhu khayrā",
    diag_thanks_sub:
      "Ihre Diagnose-Zusammenfassung wurde an unser WhatsApp gesendet. Falls WhatsApp nicht automatisch geöffnet wurde, klicken Sie unten.",
    diag_open_wa: "WhatsApp öffnen",
    diag_ans_yes: "Ja",
    diag_ans_no: "Nein",
    diag_ans_sometimes: "Manchmal",

    // Language
    lang_toggle: "English",
    lang_label_en: "English",
    lang_label_ar: "العربية",
    lang_label_ur: "اردو",
    lang_label_de: "Deutsch",
    lang_label_fr: "Français",
  },

  fr: {
    // Header
    home: "Accueil",
    ai_search: "Recherche IA",
    hijama_atlas: "Atlas de la Hijama",
    services: "Services",
    ruqyah: "Bibliothèque Ruqyah",
    academy: "Académie du Coran et de l'arabe",
    sign_in: "Se connecter",
    sign_out: "Se déconnecter",
    products: "Produits",
    contact: "Contact",
    about: "À propos",
    tagline: "Tibb al-Nabawi · 2026",

    // Disclaimer
    disclaimer_label: "Avis médical et juridique",
    disclaimer_title: "Une confiance placée entre vos mains",
    disclaimer_intro_1: "Islamic Cure & Care 2026",
    disclaimer_intro_2: "est une référence savante alliant la Tibb al-Nabawi authentique (médecine prophétique) à la recherche moderne évaluée par les pairs. Elle est fournie",
    disclaimer_intro_3: "à des fins strictement éducatives et informatives",
    disclaimer_point_1_a: "Ceci n'est",
    disclaimer_point_1_b: "pas un substitut",
    disclaimer_point_1_c: "à un diagnostic ou un traitement médical professionnel.",
    disclaimer_point_2_a: "Consultez toujours un",
    disclaimer_point_2_b: "médecin qualifié",
    disclaimer_point_2_c: "avant d'appliquer un remède, en particulier pendant la grossesse, en cas de maladie chronique ou de prise de médicaments.",
    disclaimer_point_3_a: "La Hijama (ventouses) doit être pratiquée uniquement par un",
    disclaimer_point_3_b: "praticien certifié",
    disclaimer_point_3_c: ".",
    disclaimer_point_4: "Seuls les hadiths authentiques (Sahih) et les versets coraniques vérifiés sont cités.",
    disclaimer_accept: "Je comprends et j'accepte — Entrer dans la bibliothèque",
    disclaimer_footnote: "En continuant, vous reconnaissez avoir lu et accepté cet avis.",

    // Home
    hero_title_1: "L'ancienne guérison du Prophète ﷺ,",
    hero_title_2: "validée par la science moderne.",
    hero_subtitle: "Une archive savante de remèdes authentiques de la Tibb al-Nabawi — chaque entrée est enracinée dans un Sahih Hadith et croisée avec la recherche clinique évaluée par les pairs de 2017 à 2026.",
    cta_ai: "Interroger le chercheur IA",
    cta_browse: "Parcourir la bibliothèque",
    stat_illnesses: "Maladies",
    stat_hadiths: "Hadiths Sahih",
    stat_points: "Points de Hijama",
    stat_studies: "Études modernes",

    library_label: "La bibliothèque",
    library_title: "Explorez par catégorie — du chronique au spirituel.",

    pharmacy_label: "Pharmacie prophétique",
    pharmacy_title: "Trois remèdes. Quatorze siècles de tradition.",
    pharmacy_subtitle: "Issus du Coran et de la Sunnah — préparations consignées par Ibn al-Qayyim et Ibn Sina.",

    ai_cta_pill: "Recherche de symptômes alimentée par l'IA",
    ai_cta_title: "Décrivez ce que vous ressentez — recevez des conseils enracinés dans le Coran, la Sunnah et la médecine moderne.",
    ai_cta_subtitle: "Notre assistant de recherche croise vos symptômes avec l'ensemble de la base de données pour vous renvoyer des versets coraniques, des hadiths authentiques, des correspondances de Hijama, des remèdes prophétiques et des études cliniques modernes.",
    ai_cta_btn: "Commencer une recherche",

    // Category page
    all_categories: "Toutes les catégories",
    read_full: "Lire la fiche complète",
    no_illness_in_category: "Aucune maladie dans cette catégorie pour l'instant.",

    // Illness detail
    back_to_category: "Retour à la catégorie",
    page_disclaimer: "Avis médical : cette fiche est fournie à titre éducatif uniquement. Consultez toujours un médecin qualifié avant d'appliquer un remède ou de suivre une thérapie de Hijama. Les femmes enceintes, les enfants et les patients atteints de maladies chroniques nécessitent un avis spécialisé.",
    section_revelation: "Révélation exhaustive",
    section_hijama_map: "Carte de la Hijama",
    section_pharmacy: "Pharmacie prophétique",
    section_lifestyle: "Hygiène de vie selon la Sunnah",
    section_studies: "Preuves cliniques 2026",
    studies_subtitle: "· Recherche moderne évaluée par les pairs",
    label_quran: "Coran",
    label_sahih_hadith: "Hadith authentique",
    label_preparation: "Préparation :",
    label_duration: "Durée :",
    label_source: "Source :",
    label_narrator: "Rapporteur :",
    sunnah_note: "désigne un point documenté dans la Sunnah",

    // Search
    search_pill: "Assistant de recherche IA · Claude Sonnet 4.5",
    search_title: "Décrivez vos symptômes.",
    search_subtitle: "L'assistant explore les hadiths authentiques, le Coran, les correspondances de Hijama, les remèdes prophétiques et les études modernes évaluées par les pairs de notre base de données — et renvoie les entrées pertinentes.",
    search_placeholder: "ex. : j'ai des douleurs et ballonnements abdominaux après les repas…",
    search_btn: "Rechercher",
    searching: "Recherche en cours",
    try_these: "Essayez l'un de ces exemples",
    researching: "Recherche dans la base de données…",
    researcher_note: "Note du chercheur",
    suggested_entries: "Entrées suggérées dans la bibliothèque",
    no_results: "Aucune correspondance directe trouvée. Essayez de reformuler ou explorez les catégories depuis l'accueil.",
    search_failed: "La recherche a échoué. Veuillez réessayer.",

    // Hijama Atlas
    atlas_pill: "200 points documentés",
    atlas_title: "L'Atlas de la Hijama",
    atlas_subtitle_1: "Une cartographie classique des points de ventouses compilée à partir de",
    atlas_subtitle_2: "At-Tibb An-Nabawi",
    atlas_subtitle_3: " d'Ibn al-Qayyim, du",
    atlas_subtitle_4: "Al-Qanun fi al-Tibb",
    atlas_subtitle_5: " d'Ibn Sina et des atlas modernes de pratique de la Hijama. Les points de la Sunnah sont marqués d'une étoile dorée.",
    sunnah_only: "Points de la Sunnah uniquement",
    atlas_search_placeholder: "Rechercher par nom, région ou indication…",
    points_of: "sur",
    points: "points",
    no_points_match: "Aucun point ne correspond à vos filtres.",
    region: "Région :",
    location: "Localisation :",

    // About
    method_label: "Notre méthode",
    about_title: "Une bibliothèque tenue aux plus hauts standards académiques.",
    about_intro: "Islamic Cure & Care 2026 est une archive vivante unissant l'authentique médecine prophétique (Tibb al-Nabawi) aux preuves modernes évaluées par les pairs. Voici la méthodologie derrière chaque entrée.",
    about_s1_t: "Sources authentiques uniquement",
    about_s1_b: "Chaque hadith est vérifié comme Sahih (authentique) à travers Sahih al-Bukhari, Sahih Muslim, Sunan Ibn Majah, Sunan Abi Dawud et Jami at-Tirmidhi. Nous n'incluons pas de rapports faibles (Da'if) ni fabriqués (Mawdu'). Les versets coraniques sont cités avec leur référence complète (sourate et verset).",
    about_s2_t: "Pharmacie classique",
    about_s2_b: "Les méthodes de préparation proviennent de At-Tibb An-Nabawi d'Ibn al-Qayyim et de Al-Qanun fi al-Tibb (Le Canon de la médecine) d'Ibn Sina — les textes fondamentaux de la médecine islamique depuis plus de 1 000 ans.",
    about_s3_t: "Recoupement clinique moderne",
    about_s3_b: "Lorsque cela s'applique, chaque remède est soutenu par une étude contemporaine évaluée par les pairs — citant journal, auteurs, année et DOI — issue de PubMed, du NCBI et des dépôts de recherche universitaires.",
    about_s4_t: "Conformité juridique et médicale stricte",
    about_s4_b: "Ceci est une référence éducative, et non une pratique médicale. La fenêtre d'avis qui filtre l'accès à ce site est intentionnelle. La Hijama (ventouses) doit être pratiquée uniquement par un praticien certifié. Consultez toujours un médecin qualifié.",
    about_verse_en: '« Et lorsque je suis malade, c\'est Lui qui me guérit. » — Coran 26:80',

    // Footer
    footer_intro: "Une union savante de la Tibb al-Nabawi (médecine prophétique) et de la recherche moderne fondée sur les preuves. Chaque remède est enraciné dans un Hadith authentique et vérifié par des études évaluées par les pairs.",
    footer_explore: "Explorer",
    footer_disclaimer_title: "Avis médical",
    footer_disclaimer_body_1: "Contenu fourni à des",
    footer_disclaimer_body_2: "fins éducatives uniquement",
    footer_disclaimer_body_3: ". Consultez toujours un professionnel de santé qualifié avant d'appliquer un remède. La Hijama (ventouses) doit être pratiquée par un praticien certifié.",
    footer_copyright: "© 2026 Islamic Cure & Care · Conçu avec révérence",
    footer_made_with: "Créé avec",
    footer_for_ummah: "pour la Oumma",

    // Body Diagram
    diagram_label: "Référence visuelle",
    diagram_title: "Diagramme des points de Hijama",
    print_protocol: "Imprimer le protocole",
    front_view: "Vue de face",
    back_view: "Vue de dos",
    hover_hint: "Survolez un point pour voir son nom.",
    legend_treatment: "Point de traitement",
    legend_sunnah: "Point Sunnah",
    recommended_points: "Points recommandés pour cette maladie",
    diagram_disclaimer: "★ Documenté dans la Sunnah par un hadith authentique. Les positions anatomiques sont approximatives — consultez toujours un praticien de Hijama certifié.",

    // Ruqyah recommendation
    ruqyah_rec_title: "Ruqyah recommandée pour votre guérison",
    ruqyah_rec_subtitle: "Enregistrements authentiques de Ruqyah coranique de notre clinique — écoutez avec une invocation sincère, en parallèle des remèdes prophétiques ci-dessous.",

    // Today calendar
    today_label: "Aujourd'hui",

    // Install App prompt
    install_app_pill: "Gagnez du temps",
    install_app_title: "Ajoutez Islamic Cure & Care à votre écran d'accueil",
    install_app_cta: "Installer",
    install_ios_title: "Installer sur votre iPhone",
    install_ios_step1_a: "Appuyez sur le bouton",
    install_ios_step1_b: "Partager en bas de Safari",
    install_ios_step2: "Faites défiler et appuyez sur \"Sur l'écran d'accueil\"",
    install_ios_step3: "Appuyez sur \"Ajouter\" — l'icône Al-Aqsa apparaîtra sur votre écran d'accueil",
    install_ios_close: "Compris, JazākAllāh",

    // Illness page extras
    illness_not_found: "Maladie introuvable.",
    label_surah: "Sourate",
    classical_sources_label: "Sources médicales classiques",
    source_label: "Source",
    atlas_page_label: "Page de l'atlas",
    share_on_whatsapp: "Partager sur WhatsApp",
    share_label: "Partager",
    share_copy_link: "Copier le lien",
    share_copied: "Lien copié !",
    share_ig_copied: "Copié — collez dans Instagram",
    add_to_calendar: "Ajouter à mon calendrier",
    add_to_calendar_note: "Facultatif — recevez un rappel sur votre téléphone avant chaque séance.",

    // Service condition bullets
    counselling_includes_1: "Séance privée de 45 à 60 minutes",
    counselling_includes_2: "Par vidéo (Zoom / WhatsApp) ou en présentiel",
    counselling_includes_3: "Confidentialité stricte — rien n'est partagé",
    counselling_includes_4: "Conseils fondés sur le Qur'ān & la Sunnah",
    nikah_includes_1: "Ma'dhūn qualifié officie la cérémonie",
    nikah_includes_2: "Certificat signé et tamponné fourni",
    nikah_includes_3: "Khuṭbah + 2 témoins organisés",
    nikah_includes_4: "En clinique, sur votre lieu ou en hybride",
    whats_included: "Ce qui est inclus",

    // Online Ruqyah Group
    orgroup_pill: "Nouveau · Chaque samedi",
    orgroup_title: "Groupe Ruqyah en ligne — Chaque samedi",
    orgroup_subtitle:
      "Deux séances hebdomadaires de Ruqyah en groupe, conformes à la Sharī'ah, proposées par notre clinique — rejoignez-nous depuis n'importe où dans le monde.",
    orgroup_women_pill: "Séance 1",
    orgroup_women_title: "Séance réservée aux femmes",
    orgroup_women_time: "Chaque samedi · 16h00 heure britannique",
    orgroup_women_wa:
      "Assalāmu ʿalaykum, je souhaite m'inscrire au groupe de Ruqyah en ligne réservé aux femmes (samedis à 16h heure britannique).",
    orgroup_men_pill: "Séance 2",
    orgroup_men_title: "Séance réservée aux hommes",
    orgroup_men_time: "Chaque samedi · 18h00 heure britannique",
    orgroup_men_wa:
      "Assalāmu ʿalaykum, je souhaite m'inscrire au groupe de Ruqyah en ligne réservé aux hommes (samedis à 18h heure britannique).",
    orgroup_platform: "Par appel vidéo WhatsApp · Zoom · ou Teams",
    orgroup_per_person: "par personne, par séance",
    orgroup_book_cta: "S'inscrire sur WhatsApp",
    orgroup_register_notice:
      "Les places sont limitées — veuillez vous inscrire 2–3 jours à l'avance via WhatsApp afin que nous puissions confirmer votre place et vous envoyer le lien de la réunion. Inscription uniquement par WhatsApp (appel ou message).",

    // Hijama Sunnah Days card
    sunnah_days_pill: "Prochains jours Sunnah de la Hijama",
    sunnah_days_hadith:
      "« Quiconque souhaite pratiquer la Hijama, qu'il recherche les 17ᵉ, 19ᵉ et 21ᵉ jours du mois lunaire islamique… »",
    sunnah_days_hadith_ref: "— Ṣaḥīḥ Sunan Ibn Mājah 3486",
    book_on_whatsapp: "Réserver sur WhatsApp",
    copy_reminder: "Copier le rappel",
    copied: "✓ Copié !",
    male_female_therapists: "thérapeutes hommes & femmes",

    // Ruqyah programmes
    ruqyah_programmes_pill: "Programmes d'auto-traitement",
    ruqyah_programmes_title: "Programmes Ruqyah supplémentaires",
    ruqyah_programmes_subtitle:
      "Trois protocoles traditionnels fournis par la clinique pour l'auto-traitement à domicile. Toujours combiner avec une duʿā sincère et la confiance en Allāh, le seul véritable Guérisseur.",
    section_ingredients: "Ingrédients",
    section_recite_each: "Réciter chacun 7 fois sur le mélange",
    section_method: "Méthode",
    section_additional_step: "Étape supplémentaire",
    section_caution: "⚠️ Attention",
    ask_on_whatsapp: "Demander sur WhatsApp",

    // Diagnostic CTA
    diagnostic_cta_pill: "Diagnostic spirituel gratuit",
    diagnostic_cta_title: "Remplir le questionnaire de diagnostic Ruqyah",
    diagnostic_cta_subtitle:
      "Répondez à 20 questions courtes sur vos symptômes — nous enverrons le résumé directement à notre WhatsApp pour des conseils personnalisés.",
    diagnostic_cta_btn: "Commencer le questionnaire",

    // Diagnostic page
    diag_back: "Retour à la bibliothèque Ruqyah",
    diag_title: "Questionnaire de diagnostic Ruqyah",
    diag_gender_label: "Genre *",
    diag_gender_male: "Homme",
    diag_gender_female: "Femme",

    // Islamic Marriage services
    marriage_services_pill: "Services de mariage islamique",
    marriage_services_title: "Mariage Islamique — Conseil & Nikāh",
    marriage_services_subtitle:
      "Deux services conformes à la Sharī'ah proposés par notre clinique — réservez immédiatement sur WhatsApp.",
    counselling_title: "Conseil conjugal islamique",
    counselling_desc:
      "Séances individuelles confidentielles fondées sur le Qur'ān et la Sunnah — pour couples fiancés, partenaires mariés ou familles traversant des difficultés. En visioconférence ou en présentiel.",
    counselling_book_wa_msg:
      "Assalāmu ʿalaykum, je souhaite réserver une séance de conseil conjugal islamique chez Islamic Cure & Care.",
    nikah_title: "Nikāh Islamique — Certificat de mariage",
    nikah_desc:
      "Une cérémonie de Nikāh complète et conforme à la Sharī'ah, officiée par un ma'dhūn qualifié — comprend le certificat de mariage signé et tamponné, la khuṭbah et les témoins. En clinique ou sur votre lieu de célébration.",
    nikah_book_wa_msg:
      "Assalāmu ʿalaykum, je souhaite organiser un Nikāh islamique chez Islamic Cure & Care.",
    diag_intro:
      "Que la paix soit sur vous, et la miséricorde et les bénédictions d'Allāh. Pour un diagnostic précis via le Qur'ān, la médecine prophétique, les plantes et la médecine alternative, veuillez répondre honnêtement aux questions suivantes.",
    diag_privacy_note:
      "Vos réponses seront envoyées en privé sur notre WhatsApp — aucune visibilité publique.",
    diag_name_ph: "Votre nom complet *",
    diag_phone_ph: "Téléphone (facultatif)",
    diag_email_ph: "E-mail (facultatif)",
    diag_notes_label: "Autres notes — rêves récurrents, maladies, commentaires :",
    diag_notes_ph:
      "Veuillez écrire tout ce que vous aimeriez que le praticien sache — épilepsie, autisme, cancer, rêves récurrents, etc.",
    diag_send: "Envoyer à la clinique via WhatsApp",
    diag_sending: "Envoi en cours...",
    diag_consent:
      "En soumettant, vous autorisez Islamic Cure & Care à utiliser ces réponses uniquement pour le diagnostic spirituel et médical. Nous ne partageons jamais vos données avec des tiers.",
    diag_thanks: "Envoyé — JazākAllāhu khayrā",
    diag_thanks_sub:
      "Votre résumé de diagnostic a été envoyé à notre WhatsApp. Si WhatsApp ne s'est pas ouvert automatiquement, cliquez ci-dessous.",
    diag_open_wa: "Ouvrir WhatsApp",
    diag_ans_yes: "Oui",
    diag_ans_no: "Non",
    diag_ans_sometimes: "Parfois",

    // Language
    lang_toggle: "English",
    lang_label_en: "English",
    lang_label_ar: "العربية",
    lang_label_ur: "اردو",
    lang_label_de: "Deutsch",
    lang_label_fr: "Français",
  },
};
